import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import PropTypes from 'prop-types';
import { USER_TYPES } from '@medflex/utils/constants';
import TitleBar from '@medflex/components/stories/TitleBar';
import { IconArrowLeft, IconNavigationMore, IconCopy } from '@medflex/components/stories/Icons';
import Button from '@medflex/components/stories/Button';
import PopupMenu from '@medflex/components/stories/PopupMenu';
import { showSuccessToast } from '@medflex/components/stories/Toast';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import isPrimaryClickEvent from '@medflex/utils/isPrimaryClickEvent';
import useReactRouterContext from '@medflex/hooks/useReactRouterContext';
import useClassNames from '@medflex/hooks/useClassNames';
import { IdentificationIcon } from '@heroicons/react/24/outline';
import OnlineStatus from '@medflex/components/stories/OnlineStatus';
import useOnlineStatus from '../hooks/useOnlineStatus';
import VideoMenuModule from './VideoMenuModule';
import useUserProfile from '../hooks/useUserProfile';

const Menu = ({ actionItems, chatMessagesExport }) => {
  const { t } = useTranslation('messenger');

  const handleCopy = () => {
    trackEvent({
      zone: 'ChatTitleBar',
      action: TRACKING_ACTIONS.CLICK,
      elementType: TRACKING_ELEMENT_TYPES.BUTTON,
      actionText: t('ChatTitleBarModule.copy_history'),
    });
    showSuccessToast(
      t('global:toasts.copy.title'),
      t('global:toasts.copy.text', {
        allowHtml: true,
      }),
      { toastId: 'copySuccess' },
    );
  };

  return (
    <PopupMenu>
      <PopupMenu.Menu>
        <PopupMenu.Group>
          <CopyToClipboard text={chatMessagesExport} onCopy={handleCopy}>
            <PopupMenu.Item>
              <IconCopy size="text" />
              <span className="ml-4">{t('ChatTitleBarModule.copy_history')}</span>
            </PopupMenu.Item>
          </CopyToClipboard>
        </PopupMenu.Group>
        {actionItems &&
          actionItems.map(item => (
            <PopupMenu.Group key={item.text}>
              <PopupMenu.Item onClick={item.onClick}>
                {item.icon}
                <span className="ml-4">{item.text}</span>
              </PopupMenu.Item>
            </PopupMenu.Group>
          ))}
      </PopupMenu.Menu>
      <PopupMenu.Trigger>
        {({ triggerRef }) => (
          <Button innerRef={triggerRef} shape="circle" size="sm" kind="secondary:ghost">
            <IconNavigationMore size="6" />
          </Button>
        )}
      </PopupMenu.Trigger>
    </PopupMenu>
  );
};
Menu.propTypes = {
  chatMessagesExport: PropTypes.string,
  actionItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
      onClick: PropTypes.func,
    }),
  ),
};

const ChatTitleBarModule = ({
  actionItems,
  id,
  children,
  canCall,
  height,
  className,
  name,
  chatMessagesExport,
  handleOpenInPVS,
  canOpenInPVS,
}) => {
  const { userProfile } = useUserProfile();
  const { history } = useReactRouterContext();
  const { isOnline } = useOnlineStatus(id);
  const location = useLocation();

  const cn = useClassNames(() => ['bg-white', 'flex-shrink-0', className], [className]);

  const isStaffOrPhysician = [USER_TYPES.PHYSICIAN, USER_TYPES.STAFF].includes(userProfile.type);

  return (
    <TitleBar
      className={cn}
      height={height || 'lg'}
      buttonLeft={
        <Button
          kind="primary:outline"
          shape="circle"
          className="xl:hidden"
          size="xs"
          onClick={event => {
            if (isPrimaryClickEvent(event)) {
              history.push(`/chat${location.hash ? '?filterContacts=important' : ''}`);
            }
            event.preventDefault();
          }}
        >
          <IconArrowLeft size="4" className="text-slate-600 fill-current" />
        </Button>
      }
      buttonRight={
        <>
          {isStaffOrPhysician && (
            <div className="ml-1">
              <VideoMenuModule canCall={canCall} id={id} />
            </div>
          )}
          {canOpenInPVS && (
            <div className="ml-1">
              <Button shape="circle" kind="secondary:ghost" size="sm" onClick={handleOpenInPVS}>
                <IdentificationIcon className="w-6 h-6" />
              </Button>
            </div>
          )}
          <Menu actionItems={actionItems} chatMessagesExport={chatMessagesExport} />
        </>
      }
    >
      <div className="flex items-center space-x-2 max-w-full">
        {isOnline && <OnlineStatus />}
        <div className="leading-tight min-w-0 space-y-1">
          <div className="truncate">{name}</div>
          {children}
        </div>
      </div>
    </TitleBar>
  );
};

ChatTitleBarModule.propTypes = {
  canCall: PropTypes.bool,
  chatMessagesExport: PropTypes.string,
  height: PropTypes.string,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node, PropTypes.func]),
  id: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  children: PropTypes.node,
  actionItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
      onClick: PropTypes.func,
    }),
  ),
  handleOpenInPVS: PropTypes.func,
  canOpenInPVS: PropTypes.bool,
};

ChatTitleBarModule.defaultProps = {};

export default ChatTitleBarModule;
